<template>
  <v-card
    class="mb-4 flow-step"
    flat
    color="white"
  >
    <ValidationObserver
      ref="observer"
      v-slot="{ handleSubmit }"
    >
      <v-form
        ref="form"
        @submit.prevent="handleSubmit(submit)"
        v-model="valid"
      >
        <v-container
          fluid
          class="pa-0"
        >
          <v-row align="center">
            <v-col
              v-for="(field, key) in fields"
              :key="key"
              v-show="!field.quite"
              class="py-0"
              v-bind="field.col || {cols: 12}"
            >
              <flow-field
                :key="key"
                v-bind="cleanFieldSchema(field)"
                v-model="model[field.id]"
                :flowName="flowName"
                :type="field.type"
                :name="field.name || field.title"
                :title="field.title"
                :choices="visibleChoices(field.choices)"
                :flatten-model="flattenModel"
                :auto-continue="autoContinue"
                @autoContinue="toNextStep"
              >
              </flow-field>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </ValidationObserver>
    <div v-if="!fields">
      <h5>Please fix you JSON scheme</h5>
    </div>
  </v-card>
</template>

<script>
  import FlowField from "./FlowField";
  import { _ } from 'vue-underscore';
  import {ValidationObserver} from "vee-validate";
  import {ComponentWithModelMixin, FlowGeneratorMixin} from "../../mixins/FlowGenerator";

  export default {
    name: "FlowStep",
    components: {
      FlowField,
      ValidationObserver,
    },
    mixins: [
      FlowGeneratorMixin,
      ComponentWithModelMixin
    ],
    props: {
      schema: {
        type: Object,
        required: true
      },
      flowName: {
        type: [String, undefined]
      },
      stepName: {
        type: String,
        required: true
      },
      autoContinue: {
        type: Boolean,
        default: false
      },
      flattenModel: {
        type: Object,
        required: true
      }
    },
    computed: {
      //  mapping property from schema
      ordering() {
        return this.schema.ordering
      },
      subs() {
        return this.schema.subs
      },
      cleanFieldSchema() {
        return field => _.omit(field, 'col')
      },
      // all visible fields for the current step
      // questions and visible subs
      visibleFields() {
        return [
          ...this.schema.questions,
          ..._.flatten(this.visibleSubs)
        ]
      },
      // filtered subs for the  current step
      visibleSubs () {
        return _.map(
          _.filter(
            this.subs,
            item => this.isMatchWhen(this.flattenModel, item.when)
          ), value => value.questions
        )
      },
      // filtered choices by have `when` property
      visibleChoices() {
        return choices => _.filter(
          choices,
          item => this.isMatchWhen(this.flattenModel, item.when)
        )
      },

      // main property:
      // list to render fields for the current step
      fields() {
        // check `when` within all the questions
        const filteredWhenFields = _.filter(
          this.visibleFields,
          field => !field.when || this.isMatchWhen(this.flattenModel, field.when)
        )
        // check `hide` within all the questions
        const filteredHideFields = _.filter(
          filteredWhenFields,
          field => !field.hide
        )
        // apply `ordering` when schema has it
        if (this.ordering) {
          return _.filter(
            _.map(
              this.ordering,
              value => _.find(filteredHideFields, field => field.id === value)
            ), field => field !== undefined
          )
        } else {
          return filteredHideFields
        }
      },
    },
    data() {
      return {
        valid: false,
        model: {},
      }
    },
    methods: {
      // get new fields keys and delete values from model for not existed fields
      // we also have a watcher which trigger the method on each `subs` changing
      refreshModelKeys() {
        const newKeys = _.map(this.fields, value => value.id);
        const oldKeys = _.keys(this.model);
        const differ = _.difference(oldKeys, newKeys);
        _.forEach(differ, key => delete this.model[key]);
      },
      // use it to run current form validation
      // from parent component manually and
      // to get answer about a status
      async validate(silent = false) {
        this.valid = await this.$refs.observer.validate({silent});
        return this.valid
      },
      allFieldsInputed() {
        // filter fields to check if they filled from API
        const filtering = field => {
          if (!field.quite && field.validation !== '') {
            return field
          }
        }
        const filteredFields =  _.filter(this.fields, filtering)
        const flow_keys = _.map(filteredFields, field => field.id)
        const model_keys = _.keys(this.model)
        const diff = _.difference(flow_keys, model_keys)
        return _.isEmpty(diff)
      },
      async toNextStep() {
        await this.validate(true)
        await setTimeout(() => {
          // only go to next step if all fields are filled and valid
          if (this.allFieldsInputed() && this.valid) {
            this.$emit('nextStep', this.stepName)
          }
        }, 150)
      }
    },
    watch: {
      'model': function (val) {
        this.emitter(val);
      },
      'visibleSubs': function () {
        this.refreshModelKeys()
      },
      'valid': function (validationStatus) {
        this.$emit('validationChanged', this.stepName, validationStatus)
      }
    }
  }
</script>
<style lang="scss"></style>
